export class AppRoute<
  TGroupPath extends string,
  TGroupParam extends string,
  TRelativePath extends string,
  TRelativeParam extends string,
  TSearchParams extends string,
> {
  public readonly groupPath: TGroupPath;

  public readonly groupParams: TGroupParam[];

  public readonly relativePath: TRelativePath | never;

  public readonly relativeParams: TRelativeParam[];

  public readonly searchParams: TSearchParams[];

  constructor(data: {
    groupPath: TGroupPath;
    groupParams?: TGroupParam[];
    relativePath: TRelativePath | never;
    relativeParams?: TRelativeParam[];
    searchParams?: TSearchParams[];
  }) {
    this.groupPath = data.groupPath;
    this.groupParams = data.groupParams || [];

    this.relativePath = data.relativePath;
    this.relativeParams = data.relativeParams || [];

    this.searchParams = data.searchParams || [];
  }

  get fullPath(): TRelativePath extends never
    ? TGroupPath
    : `${TGroupPath}/${TRelativePath}` {
    if (this.relativePath === '') {
      // @ts-expect-error The return type of `fullPath` is correct, but we're missing a way to check `TRelativePath is never`
      return this.groupPath;
    }

    // @ts-expect-error The return type of `fullPath` is correct, but we're missing a way to check `TRelativePath is never`
    return `${this.groupPath}/${this.relativePath}`;
  }

  /**
   * Full path with params.
   *
   * The given params & searchParams are set to the url accordingly
   */
  with(
    params: Record<TGroupParam | TRelativeParam, string | number | string[]> &
      Partial<Record<TSearchParams, string | number | string[] | boolean>>,
  ) {
    const paramKeys = Object.keys(params);

    // Url

    const pathnameKeys = paramKeys.filter(
      (paramKey) =>
        (this.groupParams as string[]).includes(paramKey) ||
        (this.relativeParams as string[]).includes(paramKey),
    ) as (TGroupParam | TRelativeParam)[];

    const pathname = pathnameKeys.reduce((currentPath: string, param) => {
      const value = params[param].toString();
      return currentPath.replace(`:${param}`, value);
    }, this.fullPath);

    // Search

    const searchKeys = Object.keys(params).filter((paramKey) =>
      (this.searchParams as string[]).includes(paramKey),
    ) as TSearchParams[];

    // @NOTE - We currently handle multiple filters by appending
    // multiple key value pairs to the URL, which makes it very
    // difficult to do safe query string manipulation.
    // Hence the manual creation of key value pairs and joining.
    const searchParams = searchKeys.reduce((acc: string[], searchKey) => {
      const value = params[searchKey];
      if (Array.isArray(value)) {
        value.forEach((v) => acc.push(`${searchKey}=${v}`));
      } else {
        acc.push(`${searchKey}=${value}`);
      }
      return acc;
    }, []);

    const search = searchParams.join('&');
    return search ? `${pathname}?${search}` : pathname;
  }
}
